import { ChangeEvent, ReactNode, useEffect, useRef, useState } from 'react';
import CaretUpDown from '@components/Icons/CaretUpDown';
import classNames from 'classnames';
import CustomSelect from '@components/FormFields/CustomSelect';
import { StatusIcons, CustomStatus } from '@typings/api-models';
import s from './WorkflowOptionSelect.module.scss';
import CustomStatusIcon from '../DesignLibrary/CustomStatusIcons';

type WorkflowOptionSelectType = {
  currentOption: string;
  isAttorneyCase: boolean;
  enableMaintainStatus?: boolean;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  fullWidth?: boolean;
  label?: string;
  statuses: Array<CustomStatus> | undefined;
};

const WorkflowOptionSelect: React.FC<WorkflowOptionSelectType> = ({
  currentOption, isAttorneyCase, enableMaintainStatus = false, onChange, fullWidth, label, statuses,
}) => {
  const [labelWidth, setLabelWidth] = useState('auto');
  const textRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setLabelWidth(`${textRef.current.offsetWidth + (currentOption === 'archived' ? 44 : 56)}px`);
    }
  }, [currentOption]);

  const getOptionComponent = ({ icon, colour, title }: { icon: StatusIcons, colour: string, title: string }): ReactNode => (
    <div className={s.item}>
      <CustomStatusIcon iconKey={icon} colour={colour}/> {title}
    </div>
  );

  if (!statuses) {
    return (
      <span className='caption'>Error fetching workflow status</span>
    );
  }

  const currentStatus = statuses.find((status) => status.slug === currentOption) ?? (currentOption === 'maintain-status'
    ? {
      colour: 'var(--color-gray-75)',
      creatorId: null,
      icon: 'Empty' as StatusIcons,
      id: null,
      slug: 'maintain-status',
      title: 'Maintain Status',
    }
    : null);

  if (!currentStatus) {
    return (
      <span className='caption'>Error fetching workflow status</span>
    );
  }

  const selectableOptions = statuses
    .filter((status) => (isAttorneyCase || ( status.slug !== 'preparing-case' && status.slug !== 'filed-and-pending')))
    .map((status) => ({ title: getOptionComponent({ colour: status.colour, icon: status.icon, title: status.title }), value: status.slug }));
  if(enableMaintainStatus) {
    selectableOptions.push({ title: getOptionComponent({ colour: '#666666', icon: 'Empty', title: 'Maintain Status'}), value: '' });
  }

  return (
    <>
      <span ref={textRef} className={s.measureOptionLength}>
        {currentStatus.title}
      </span>
      <CustomSelect
        id="workflow-status"
        disabled={currentOption === 'archived'}
        label={label || ''}
        trigger={
          <div
            className={classNames(s.option, {
              [s.optionArchived]: currentOption === 'archived',
              [s.optionSelectable]: currentOption !== 'archived',
            })}
            style={{
              boxShadow: `inset 0 0 0 1px ${currentStatus.colour}`,
              color: currentStatus.colour,
              minWidth: labelWidth,
              width: fullWidth ? '100%' : labelWidth,
            }}
          >
            <CustomStatusIcon iconKey={currentStatus.icon} colour={currentStatus.colour}/>
            <span className={s.currentOption}>{currentStatus.title}</span>
            <div className={s.bg} style={{backgroundColor: currentStatus.colour}} />
            {currentOption !== 'archived' && <CaretUpDown size={12} className={s.selectIcon} />}
          </div>
        }
        onChange={e =>
          onChange({
            target: {
              value: e,
            },
          } as ChangeEvent<HTMLSelectElement>)
        }
        value={currentOption}
        options={selectableOptions}
      />
    </>
  );
};

export default WorkflowOptionSelect;
